import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Progress } from '../ui/progress';
import { Globe, ArrowLeft, Award, Trophy, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { programAPI } from '../../services/programApi';
import { toast } from '../../hooks/use-toast';

const FinalTest = () => {
  const navigate = useNavigate();
  const { language } = useParams();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast({
        title: 'Login Required',
        description: 'Please login to take the final test.',
        variant: 'destructive',
      });
      navigate('/login');
      return;
    }
    generateTest();
  }, [language, navigate]);

  const generateTest = async () => {
    setLoading(true);
    try {
      const data = await programAPI.generateTest(language, null, true); // is_final = true
      setQuestions(data.questions || []);
    } catch (error) {
      console.error('Error generating test:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate final test. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionIndex, answerIndex) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [questionIndex]: answerIndex
    });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmitTest = async () => {
    // Check if all questions are answered
    const answeredCount = Object.keys(selectedAnswers).length;
    if (answeredCount < questions.length) {
      toast({
        title: 'Incomplete Test',
        description: `Please answer all questions. ${questions.length - answeredCount} remaining.`,
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await programAPI.submitTest(language, selectedAnswers, null, true);
      setResults(response);
      setShowResults(true);
      
      if (response.passed) {
        toast({
          title: 'Congratulations!',
          description: `You passed the final test with ${response.score}%!`,
        });
      } else {
        toast({
          title: 'Keep Practicing',
          description: `You scored ${response.score}%. You need 70% to pass.`,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error submitting test:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit test. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetake = () => {
    setShowResults(false);
    setSelectedAnswers({});
    setCurrentQuestion(0);
    setResults(null);
    generateTest();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-amber-50 via-white to-sky-50">
        <Award className="h-16 w-16 text-amber-600 animate-pulse mb-4" />
        <p className="text-xl text-slate-600">Generating your personalized final test...</p>
        <p className="text-sm text-slate-500 mt-2">This may take up to a minute</p>
      </div>
    );
  }

  // Handle case when questions failed to load
  if (!questions || questions.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-sky-50">
        <nav className="border-b bg-white/80 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-4">
                <Button variant="ghost" size="icon" onClick={() => navigate(`/program/${language}`)}>
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="flex items-center space-x-2">
                  <Globe className="h-8 w-8 text-amber-600" />
                  <span className="text-xl font-bold text-slate-900">Final Test</span>
                </div>
              </div>
            </div>
          </div>
        </nav>
        <div className="max-w-xl mx-auto px-4 py-16 text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Unable to Generate Test</h1>
          <p className="text-slate-600 mb-6">
            Please make sure you're logged in and try again. If the problem persists, go back to the program and try again.
          </p>
          <div className="flex justify-center space-x-4">
            <Button variant="outline" onClick={() => navigate(`/program/${language}`)}>
              Back to Program
            </Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={generateTest}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (showResults && results) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-sky-50">
        <nav className="border-b bg-white/80 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-4">
                <Button variant="ghost" size="icon" onClick={() => navigate(`/program/${language}`)}>
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="flex items-center space-x-2">
                  <Globe className="h-8 w-8 text-amber-600" />
                  <span className="text-xl font-bold text-slate-900">Final Test Results</span>
                </div>
              </div>
            </div>
          </div>
        </nav>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className={`border-4 ${results.passed ? 'border-emerald-400 bg-emerald-50' : 'border-amber-400 bg-amber-50'}`}>
            <CardContent className="p-8 text-center">
              {results.passed ? (
                <Trophy className="h-24 w-24 text-yellow-500 mx-auto mb-6" />
              ) : (
                <Award className="h-24 w-24 text-amber-600 mx-auto mb-6" />
              )}
              
              <h1 className={`text-4xl font-bold mb-4 ${results.passed ? 'text-emerald-700' : 'text-amber-700'}`}>
                {results.passed ? 'Congratulations!' : 'Keep Learning!'}
              </h1>
              
              <p className="text-6xl font-bold text-slate-900 mb-4">{results.score}%</p>
              <p className="text-xl text-slate-600 mb-2">
                {results.correct_count} of {results.total_questions} correct
              </p>
              <p className="text-slate-500 mb-8">
                {results.passed 
                  ? `You've mastered the ${language.charAt(0).toUpperCase() + language.slice(1)} 15-day program!`
                  : 'You need 70% to pass. Review the lessons and try again.'}
              </p>

              <div className="flex justify-center space-x-4">
                <Button 
                  variant="outline" 
                  onClick={() => navigate(`/program/${language}`)}
                >
                  Back to Program
                </Button>
                {!results.passed && (
                  <Button 
                    className="bg-amber-600 hover:bg-amber-700"
                    onClick={handleRetake}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Retake Test
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Question Review */}
          <div className="mt-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Question Review</h2>
            <div className="space-y-4">
              {results.explanations?.map((item, index) => (
                <Card key={index} className={`border-2 ${item.is_correct ? 'border-emerald-200' : 'border-red-200'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start space-x-3">
                      {item.is_correct ? (
                        <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-1" />
                      ) : (
                        <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
                      )}
                      <div>
                        <p className="font-medium text-slate-900">Q{index + 1}: {item.question}</p>
                        <p className="text-sm text-slate-600 mt-1">
                          Your answer: <span className={item.is_correct ? 'text-emerald-600' : 'text-red-600'}>
                            {item.your_answer}
                          </span>
                        </p>
                        {!item.is_correct && (
                          <p className="text-sm text-emerald-600 mt-1">
                            Correct answer: {item.correct_answer}
                          </p>
                        )}
                        <p className="text-sm text-slate-500 mt-2 italic">{item.explanation}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  const progress = questions.length > 0 ? ((currentQuestion + 1) / questions.length) * 100 : 0;
  const answeredCount = Object.keys(selectedAnswers).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-sky-50">
      {/* Navigation */}
      <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(`/program/${language}`)}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center space-x-2">
                <Globe className="h-8 w-8 text-amber-600" />
                <span className="text-xl font-bold text-slate-900">
                  {language.charAt(0).toUpperCase() + language.slice(1)} Final Test
                </span>
              </div>
            </div>
            <div className="text-sm text-slate-600">
              {answeredCount}/{questions.length} answered
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-600">Question {currentQuestion + 1} of {questions.length}</span>
            <span className="text-sm text-slate-600">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question Card */}
        {currentQ && (
          <Card className="border-2 mb-6">
            <CardHeader>
              <CardTitle className="text-xl">Question {currentQuestion + 1}</CardTitle>
              <CardDescription className="text-lg text-slate-800">
                {currentQ.question}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {currentQ.options.map((option, index) => (
                  <button
                    key={index}
                    data-testid={`answer-option-${index}`}
                    className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
                      selectedAnswers[currentQuestion] === index
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-slate-200 hover:border-amber-300 hover:bg-slate-50'
                    }`}
                    onClick={() => handleAnswerSelect(currentQuestion, index)}
                  >
                    <span className={`font-medium ${
                      selectedAnswers[currentQuestion] === index ? 'text-amber-700' : 'text-slate-700'
                    }`}>
                      {String.fromCharCode(65 + index)}. {option}
                    </span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
          >
            Previous
          </Button>

          <div className="flex items-center space-x-2">
            {/* Question dots */}
            <div className="hidden sm:flex space-x-1">
              {questions.map((_, index) => (
                <button
                  key={index}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentQuestion
                      ? 'bg-amber-600'
                      : selectedAnswers[index] !== undefined
                      ? 'bg-emerald-500'
                      : 'bg-slate-300'
                  }`}
                  onClick={() => setCurrentQuestion(index)}
                />
              ))}
            </div>
          </div>

          {currentQuestion < questions.length - 1 ? (
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              onClick={handleNext}
            >
              Next
            </Button>
          ) : (
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handleSubmitTest}
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit Test'}
            </Button>
          )}
        </div>

        {/* Answer Summary */}
        <div className="mt-8 p-4 bg-slate-100 rounded-lg">
          <p className="text-sm text-slate-600 text-center">
            {answeredCount === questions.length 
              ? '✓ All questions answered. Ready to submit!'
              : `Answer all ${questions.length} questions to submit the test.`}
          </p>
        </div>
      </div>
    </div>
  );
};

export default FinalTest;
